import React, { useEffect, useState } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import { API_BASE } from "../../../../consts";
import authService from "../../../../services/AuthService";

const api = (path, opts) => authService.authenticatedFetch(`${API_BASE}${path}`, opts);
const money = (v) => (v === null || v === undefined ? "-" : `$${parseFloat(v).toFixed(2)}`);

const ReplensModal = ({ item, onClose, onChanged }) => {
    const [replens, setReplens] = useState([]);
    const [nextUnitCost, setNextUnitCost] = useState(null);
    const [fallbackUnitCost, setFallbackUnitCost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [draft, setDraft] = useState({ quantity: "", cost_price: "", purchase_date: "", supplier: "", expiration_date: "", notes: "" });
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api(`/api/catalog/${item.id}/lots`)
            .then((r) => r.json())
            .then((d) => {
                setReplens(d.lots || []);
                setNextUnitCost(d.next_unit_cost);
                setFallbackUnitCost(d.fallback_unit_cost);
            })
            .catch(() => setError("Failed to load replens"))
            .finally(() => setLoading(false));
    };

    useEffect(load, [item.id]);

    const addReplen = async () => {
        if (!draft.quantity || !draft.cost_price) return;
        setSaving(true);
        setError("");
        try {
            const resp = await api(`/api/catalog/${item.id}/lots`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(draft),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.error || "Failed to add replen");
            setDraft({ quantity: "", cost_price: "", purchase_date: "", supplier: "", expiration_date: "", notes: "" });
            load();
            onChanged && onChanged();
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    const deleteReplen = async (lotId) => {
        if (!window.confirm("Delete this replen? This affects FIFO cost calculations in reports.")) return;
        await api(`/api/catalog/${item.id}/lots/${lotId}`, { method: "DELETE" });
        load();
        onChanged && onChanged();
    };

    const totalRemaining = replens.reduce((s, l) => s + (l.remaining_quantity || 0), 0);

    return (
        <div className="fixed inset-0 bg-black/60 z-[999999] flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-teal-500 to-blue-600 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-white font-bold text-lg">Configure Replens</h2>
                        <p className="text-teal-100 text-xs mt-0.5 truncate max-w-md">{item.title || item.sku}</p>
                    </div>
                    <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20} /></button>
                </div>

                <div className="p-5 overflow-y-auto flex-1">
                    <p className="text-xs text-gray-500 mb-4">
                        Every time you buy more inventory at a different cost, add it here as a new replen.
                        Reports use FIFO - oldest replen's cost is used first as units sell. Anything this history
                        can't explain falls back to your catalog's current Cost Price ({money(fallbackUnitCost)}/unit) - never blocks or breaks a report.
                    </p>

                    {!loading && replens.length > 0 && (
                        <div className="bg-gray-50 rounded-xl p-3 mb-4 flex items-center justify-between text-xs">
                            <span className="text-gray-500">{totalRemaining} unit(s) remaining across {replens.length} replen(s)</span>
                            <span className="text-gray-500">Next sale uses: <span className="font-bold text-teal-700">{money(nextUnitCost)}</span>/unit</span>
                        </div>
                    )}

                    {loading ? (
                        <p className="text-sm text-gray-400 text-center py-6">Loading...</p>
                    ) : replens.length === 0 ? (
                        <p className="text-sm text-gray-400 text-center py-6">No replens yet - every report currently uses your catalog's flat Cost Price ({money(fallbackUnitCost)}). Add one below to start tracking real purchase history.</p>
                    ) : (
                        <div className="border border-gray-100 rounded-xl overflow-hidden mb-4">
                            <table className="w-full text-xs">
                                <thead className="bg-gray-50 text-gray-400 uppercase">
                                    <tr>
                                        <th className="px-3 py-2 text-left">Purchase Date</th>
                                        <th className="px-3 py-2 text-right">Qty</th>
                                        <th className="px-3 py-2 text-right">Remaining</th>
                                        <th className="px-3 py-2 text-right">Cost/Unit</th>
                                        <th className="px-3 py-2 text-left">Supplier</th>
                                        <th className="px-3 py-2 text-left">Expiration</th>
                                        <th className="px-3 py-2 text-left">Source</th>
                                        <th className="px-3 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {replens.map((l) => (
                                        <tr key={l.id} className={`border-t border-gray-50 ${l.remaining_quantity <= 0 ? "opacity-40" : ""}`}>
                                            <td className="px-3 py-2">{l.purchase_date || "-"}</td>
                                            <td className="px-3 py-2 text-right">{l.quantity}</td>
                                            <td className="px-3 py-2 text-right font-semibold">{l.remaining_quantity}</td>
                                            <td className="px-3 py-2 text-right">{money(l.cost_price)}</td>
                                            <td className="px-3 py-2">{l.supplier || "-"}</td>
                                            <td className="px-3 py-2">{l.expiration_date || "-"}</td>
                                            <td className="px-3 py-2 text-[10px] text-gray-400 uppercase">{l.source}</td>
                                            <td className="px-3 py-2 text-right">
                                                <button onClick={() => deleteReplen(l.id)} className="text-red-400 hover:text-red-600">
                                                    <Trash2 size={13} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="border-2 border-dashed border-teal-200 rounded-xl p-3">
                        <p className="text-xs font-bold text-gray-600 mb-2">Add a replen</p>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                            <div>
                                <label className="text-[10px] text-gray-400 mb-0.5 block">Quantity *</label>
                                <input type="number" placeholder="Quantity" value={draft.quantity}
                                    onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-400 mb-0.5 block">Cost / unit *</label>
                                <input type="number" placeholder="Cost" value={draft.cost_price}
                                    onChange={(e) => setDraft((d) => ({ ...d, cost_price: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-400 mb-0.5 block">Purchase Date</label>
                                <input type="date" value={draft.purchase_date}
                                    onChange={(e) => setDraft((d) => ({ ...d, purchase_date: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-400 mb-0.5 block">Supplier</label>
                                <input type="text" placeholder="Supplier" value={draft.supplier}
                                    onChange={(e) => setDraft((d) => ({ ...d, supplier: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-400 mb-0.5 block">Expiration Date</label>
                                <input type="date" value={draft.expiration_date}
                                    onChange={(e) => setDraft((d) => ({ ...d, expiration_date: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-400 mb-0.5 block">Notes</label>
                                <input type="text" placeholder="Notes" value={draft.notes}
                                    onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs" />
                            </div>
                        </div>
                        <button onClick={addReplen} disabled={saving || !draft.quantity || !draft.cost_price}
                            className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-bold hover:bg-teal-700 disabled:opacity-50">
                            <Plus size={13} /> Add Replen
                        </button>
                    </div>

                    {error && <p className="text-xs text-red-500 mt-2">{error}</p>}
                </div>
            </div>
        </div>
    );
};

export default ReplensModal;